import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, API, formatApiError, mediaUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import PartnerCommission from "@/components/PartnerCommission";
import PartnerProducts from "@/components/PartnerProducts";
import {
  Copy, Download, IndianRupee, MessageCircle, QrCode, ShoppingBag, Stethoscope, Users,
} from "lucide-react";

const money = (v) => `₹${Number(v || 0).toFixed(0)}`;
const when = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }) : "—";

const Metric = ({ name, value, icon: Icon, testid }) => (
  <div className="rounded-xl border border-border p-4" data-testid={testid}>
    <div className="overline flex items-center gap-1.5"><Icon size={12} /> {name}</div>
    <div className="font-display text-2xl mt-1">{value}</div>
  </div>
);

const KIND_TONE = {
  prescription: "border-sky-500/40 text-sky-700 bg-sky-50",
  appointment: "border-amber-500/50 text-amber-700 bg-amber-500/10",
  order: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
};

/**
 * The doctor partner's own dashboard: their link and QR, what it produced month by
 * month, who came in through it, and what the clinic has paid them. Nothing here can
 * reach another doctor's figures or the clinic's own settings.
 */
export default function PartnerDashboard() {
  const [summary, setSummary] = useState(null);
  const [patients, setPatients] = useState([]);
  const [payments, setPayments] = useState({ payments: [], total: 0 });
  const [partner, setPartner] = useState(null);
  const [profile, setProfile] = useState({ clinic_name: "", clinic_address: "", bio: "", photo: "" });

  const load = () => {
    api.get("/partner/summary").then(({ data }) => setSummary(data)).catch((e) =>
      toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/partner/patients").then(({ data }) => setPatients(data.patients || [])).catch(() => {});
    api.get("/partner/payments").then(({ data }) => setPayments(data)).catch(() => {});
    api.get("/partner/me").then(({ data }) => {
      setPartner(data.partner);
      setProfile({
        clinic_name: data.partner.clinic_name || "",
        clinic_address: data.partner.clinic_address || "",
        bio: data.partner.bio || "",
        photo: "",
      });
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const copyLink = () => {
    navigator.clipboard?.writeText(summary.link);
    toast.success("Your referral link is copied");
  };

  // Generated by our own backend — no third party ever sees the doctor's link.
  const qrUrl = `${API}/partner/qr.png`;

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/partner/profile", profile);
      setPartner(data.partner);
      toast.success("Sent to the clinic for approval");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setAlert = async (key, value) => {
    const alerts = { ...(partner.alerts || {}), [key]: value };
    try {
      await api.put("/partner/alerts", alerts);
      setPartner((p) => ({ ...p, alerts }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setProfile((p) => ({ ...p, photo: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  if (!summary || !partner)
    return <div className="p-12 text-center font-display text-2xl">Loading…</div>;

  const stats = summary.stats || {};

  return (
    <main className="max-w-5xl mx-auto px-5 py-12 space-y-6" data-testid="partner-dashboard">
      <div>
        <p className="overline flex items-center gap-2"><Stethoscope size={14} /> Doctor partner</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{partner.name}</h1>
        <p className="text-base text-muted-foreground mt-1">
          {partner.clinic_name} · code <span className="font-mono">{summary.code}</span>
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to="/dashboard">
            <Button variant="outline" className="btn-pill h-9" data-testid="partner-to-patient-dashboard">
              <ShoppingBag size={13} className="mr-1.5" /> My orders & profile
            </Button>
          </Link>
          <Link to="/chat">
            <Button variant="outline" className="btn-pill h-9" data-testid="partner-to-chat">
              <MessageCircle size={13} className="mr-1.5" /> Chat with the clinic
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric name="QR scans" value={stats.scans || 0} icon={QrCode} testid="partner-stat-scans" />
        <Metric name="Patients referred" value={(stats.prescriptions || 0) + (stats.appointments || 0)}
                icon={Users} testid="partner-stat-referrals" />
        <Metric name="Orders paid" value={stats.paid_orders || 0} icon={ShoppingBag} testid="partner-stat-orders" />
        <Metric name="Revenue" value={money(stats.revenue)} icon={IndianRupee} testid="partner-stat-revenue" />
      </div>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h2 className="font-display text-2xl mb-1">Your link and QR</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Print the QR for your clinic or send the link on WhatsApp. Every patient who uses it is
            recorded against your code.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            {qrUrl && (
              <img src={qrUrl} alt="Your referral QR" className="w-32 h-32 rounded-xl border border-border bg-white p-2"
                   data-testid="partner-qr-image" />
            )}
            <div className="space-y-2">
              <code className="block text-xs bg-muted/60 rounded-lg px-3 py-2 break-all max-w-md"
                    data-testid="partner-link">{summary.link}</code>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="btn-pill h-9" onClick={copyLink}
                        data-testid="partner-copy-link">
                  <Copy size={12} className="mr-1.5" /> Copy link
                </Button>
                <a href={qrUrl} download={`${summary.code}-qr.png`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="btn-pill h-9" data-testid="partner-qr-download">
                    <Download size={12} className="mr-1.5" /> Download QR
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h2 className="font-display text-2xl mb-4">Month by month</h2>
          <div className="h-64" data-testid="partner-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary.months || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip formatter={(value, name) => (name === "revenue" ? money(value) : value)} />
                <Bar dataKey="revenue" name="revenue" fill="#215334" radius={[6, 6, 0, 0]} />
                <Bar dataKey="referrals" name="referrals" fill="#c8631f" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h2 className="font-display text-2xl mb-1">Patients you referred</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Names and numbers only — prescriptions and medical notes stay with the clinic.
          </p>
          {patients.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="partner-patients-empty">
              Nobody has used your link yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="partner-patients-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-normal">Date</th>
                    <th className="py-2 pr-4 font-normal">Patient</th>
                    <th className="py-2 pr-4 font-normal">Came for</th>
                    <th className="py-2 pr-4 font-normal">Status</th>
                    <th className="py-2 font-normal">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((row, index) => (
                    <tr key={`${row.kind}-${index}`} className="border-b border-border/60">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground">{when(row.at)}</td>
                      <td className="py-2.5 pr-4">
                        <div>{row.name}</div>
                        <div className="text-xs text-muted-foreground">{row.phone}</div>
                      </td>
                      <td className="py-2.5 pr-4">
                        <Badge variant="outline" className={`${KIND_TONE[row.kind]} text-[10px] capitalize`}>
                          {row.kind}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-4 capitalize">{row.status || "—"}</td>
                      <td className="py-2.5">{row.amount ? money(row.amount) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PartnerCommission />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardContent className="p-6 lg:p-8">
            <h2 className="font-display text-2xl mb-1">Referral payments received</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Entered by the clinic against your referrals. {money(payments.total)} recorded so far.
              Product-share payments are listed separately below.
            </p>
            {(payments.payments || []).length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="partner-payments-empty">
                No payments recorded yet.
              </p>
            ) : (
              <div className="space-y-1" data-testid="partner-payments-list">
                {payments.payments.map((entry) => (
                  <div key={entry.id} className="flex justify-between text-sm py-1.5 border-b border-border/60">
                    <span className="text-muted-foreground">{entry.paid_on} · {entry.note || "—"}</span>
                    <span>{money(entry.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6 lg:p-8 space-y-4">
            <div>
              <h2 className="font-display text-2xl mb-1">Your clinic profile</h2>
              <p className="text-sm text-muted-foreground">
                Changes go to the clinic for approval before they appear anywhere.
              </p>
              {partner.profile_status === "pending_review" && (
                <Badge variant="outline" className="mt-2 border-amber-500/50 text-amber-700 bg-amber-500/10"
                       data-testid="partner-profile-pending">
                  Waiting for the clinic's approval
                </Badge>
              )}
            </div>
            {partner.photo && (
              <img src={mediaUrl(partner.photo)} alt="Clinic" className="w-20 h-20 rounded-xl object-cover border border-border" />
            )}
            <Input value={profile.clinic_name} placeholder="Clinic name" className="bg-transparent"
                   onChange={(e) => setProfile((p) => ({ ...p, clinic_name: e.target.value }))}
                   data-testid="partner-profile-clinic" />
            <Input value={profile.clinic_address} placeholder="Clinic address" className="bg-transparent"
                   onChange={(e) => setProfile((p) => ({ ...p, clinic_address: e.target.value }))}
                   data-testid="partner-profile-address" />
            <Textarea value={profile.bio} placeholder="A short bio patients can read" rows={3} className="bg-transparent"
                      onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
                      data-testid="partner-profile-bio" />
            <input type="file" accept="image/*" onChange={pickPhoto} className="text-xs"
                   data-testid="partner-profile-photo" />
            <Button className="btn-action h-10" onClick={saveProfile} data-testid="partner-profile-save">
              Send for approval
            </Button>

            <div className="pt-4 border-t border-border space-y-3">
              <p className="overline">Alerts for new referrals</p>
              {[["whatsapp", "On WhatsApp"], ["email", "By email"]].map(([key, label]) => (
                <label key={key} className="flex items-center justify-between text-sm">
                  {label}
                  <Switch
                    checked={Boolean(partner.alerts?.[key])}
                    onCheckedChange={(v) => setAlert(key, v)}
                    data-testid={`partner-alert-${key}`}
                  />
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <PartnerProducts />
    </main>
  );
}
